@file:Suppress("MagicNumber", "LongMethod", "MatchingDeclarationName")

package com.nonopn.skydome.common.app.pages.documents

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Search
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.material3.TabRow
import androidx.compose.material3.TabRowDefaults
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouteCertNsg
import com.nonopn.skydome.common.app.navigation.RouteKeraunicReport
import com.nonopn.skydome.common.app.navigation.RouterCertNsg
import com.nonopn.skydome.common.app.navigation.RouterKeraunicReport
import com.nonopn.skydome.common.app.navigation.RouterMap
import com.nonopn.skydome.common.app.navigation.RouteCertFoudr
import com.nonopn.skydome.common.app.navigation.RouterCertFoudr
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryType
import korlibs.time.DateTime
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.documents_all_years
import com.nonopn.skywatcher.common.app.res.documents_badge
import com.nonopn.skywatcher.common.app.res.documents_last
import com.nonopn.skywatcher.common.app.res.documents_no_document
import com.nonopn.skywatcher.common.app.res.documents_open
import com.nonopn.skywatcher.common.app.res.documents_show_more
import com.nonopn.skywatcher.common.app.res.documents_new_button
import com.nonopn.skywatcher.common.app.res.documents_no_site
import com.nonopn.skywatcher.common.app.res.documents_search_hint
import com.nonopn.skywatcher.common.app.res.documents_unknown_error
import com.nonopn.skywatcher.common.app.res.documents_unnamed_site
import com.nonopn.skywatcher.common.app.res.menu_sr_cert_nsg
import com.nonopn.skywatcher.common.app.res.menu_sr_certificates
import com.nonopn.skywatcher.common.app.res.menu_sr_keraunic
import com.nonopn.skywatcher.design.theme.AppColor

import eu.codlab.compose.widgets.TextNormal
import eu.codlab.viewmodel.rememberViewModel
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

// ──────────────────────────────────────────────
// Tab enum — 3 document types
// ──────────────────────────────────────────────

enum class TabOption(val tabTitle: StringResource, val type: DocumentHistoryType) {
    KERAUNIC_REPORT(Res.string.menu_sr_keraunic, DocumentHistoryType.KERAUNIC_REPORT),
    CERT_NSG(Res.string.menu_sr_cert_nsg, DocumentHistoryType.CERT_NSG),
    CERT_LIGHTNING(Res.string.menu_sr_certificates, DocumentHistoryType.CERT_LIGHTNING),
}

// ──────────────────────────────────────────────
// Entry point
// ──────────────────────────────────────────────

@Composable
fun DocumentsScreen(
    modifier: Modifier = Modifier,
) {
    val appModel: AppModel = LocalApp.current
    val model = rememberViewModel { DocumentsModel(appModel) }
    val state by model.states.collectAsState()

    var selectedTab by remember { mutableStateOf(TabOption.KERAUNIC_REPORT) }
    // The search can be prefilled by the dashboard map pop-up (consumed once)
    var searchQuery by remember {
        mutableStateOf(appModel.pendingDocumentsSearch.orEmpty().also { appModel.pendingDocumentsSearch = null })
    }
    // The year filter is specific to each tab
    var selectedYear by remember(selectedTab) { mutableStateOf<Int?>(null) }

    val years = remember(state.history, selectedTab) {
        availableYears(state.history, selectedTab.type, ::documentYear)
    }
    // A year can disappear when the history is refreshed
    val activeYear = selectedYear?.takeIf { years.contains(it) }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(AppColor.StrikeBackground)
    ) {
        // Header: search bar + "Nouveau document" button
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppColor.StrikeCard)
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            OutlinedTextField(
                modifier = Modifier.weight(1f),
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = {
                    TextNormal(
                        text = stringResource(Res.string.documents_search_hint),
                        fontSize = 13.sp,
                        color = AppColor.StrikeText.copy(alpha = 0.4f)
                    )
                },
                leadingIcon = {
                    Icon(
                        imageVector = Icons.Rounded.Search,
                        contentDescription = null,
                        tint = AppColor.StrikeText.copy(alpha = 0.5f)
                    )
                },
                singleLine = true,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AppColor.StrikePrimary,
                    unfocusedBorderColor = AppColor.StrikeText.copy(alpha = 0.2f)
                )
            )

            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(10.dp))
                    .background(AppColor.StrikePrimary)
                    .clickable(
                        indication = null,
                        interactionSource = remember { MutableInteractionSource() },
                        onClick = { appModel.show(RouterMap.navigateTo()) }
                    )
                    .padding(horizontal = 14.dp, vertical = 14.dp),
                contentAlignment = Alignment.Center
            ) {
                TextNormal(
                    text = stringResource(Res.string.documents_new_button),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Color.White
                )
            }
        }

        TabRow(
            selectedTabIndex = selectedTab.ordinal,
            containerColor = AppColor.StrikeCard,
            contentColor = AppColor.StrikePrimary,
            indicator = { tabPositions ->
                TabRowDefaults.SecondaryIndicator(
                    modifier = Modifier.tabIndicatorOffset(tabPositions[selectedTab.ordinal]),
                    color = AppColor.StrikePrimary
                )
            }
        ) {
            TabOption.entries.forEach { tab ->
                Tab(
                    selected = selectedTab == tab,
                    onClick = { selectedTab = tab },
                    text = {
                        TextNormal(
                            text = stringResource(tab.tabTitle),
                            fontSize = 13.sp,
                            fontWeight = if (selectedTab == tab) {
                                FontWeight.Bold
                            } else {
                                FontWeight.Normal
                            },
                            color = if (selectedTab == tab) {
                                AppColor.StrikePrimary
                            } else {
                                AppColor.StrikeText.copy(alpha = 0.6f)
                            }
                        )
                    }
                )
            }
        }

        if (years.isNotEmpty()) {
            YearFilterRow(
                years = years,
                selectedYear = activeYear,
                onYearSelected = { selectedYear = it }
            )
        }

        val known = remember(state.projects) {
            state.projects.map { KnownSite(it.uuid, it.name, it.latitude, it.longitude) }
        }

        when (selectedTab) {
            TabOption.CERT_LIGHTNING -> LightningCertificatesTab(
                state = state,
                known = known,
                searchQuery = searchQuery,
                year = activeYear,
                onOpenCertificate = { item -> openDocument(appModel, item) }
            )
            else -> ProjectListTab(
                state = state,
                known = known,
                type = selectedTab.type,
                documentLabel = selectedTab.tabTitle,
                searchQuery = searchQuery,
                year = activeYear,
                onOpenDocument = { item -> openDocument(appModel, item) }
            )
        }
    }
}

/** Reopens a generated document with the same screen (and the same data) as when it was generated. */
private fun openDocument(appModel: AppModel, item: DocumentHistoryItem) {
    val projectUuid = item.projectUuid()
    val navigateTo = when (item.type) {
        DocumentHistoryType.KERAUNIC_REPORT -> RouterKeraunicReport.navigateTo(
            RouteKeraunicReport(
                projectUuid = projectUuid,
                projectName = item.siteName,
                lat = item.latitude ?: 0.0,
                lon = item.longitude ?: 0.0,
            )
        )

        DocumentHistoryType.CERT_NSG -> RouterCertNsg.navigateTo(
            RouteCertNsg(
                projectUuid = projectUuid,
                projectName = item.siteName,
                lat = item.latitude ?: 0.0,
                lon = item.longitude ?: 0.0,
            )
        )

        DocumentHistoryType.CERT_LIGHTNING -> RouterCertFoudr.navigateTo(
            RouteCertFoudr(
                projectUuid = projectUuid,
                projectName = item.siteName,
                projectLat = item.latitude ?: 0.0,
                projectLon = item.longitude ?: 0.0,
                impactLat = item.impactLat ?: 0.0,
                impactLon = item.impactLon ?: 0.0,
                impactTimestamp = item.impactTimestamp,
                impactAmplitude = item.impactAmplitude,
                impactPercent = item.impactPercent,
                impactExternalId = item.impactExternalId,
            )
        )
    }

    appModel.show(navigateTo)
}

// ══════════════════════════════════════════════
// Year filter — shared by the 3 tabs
// ══════════════════════════════════════════════

@Composable
private fun YearFilterRow(
    years: List<Int>,
    selectedYear: Int?,
    onYearSelected: (Int?) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        YearChip(
            label = stringResource(Res.string.documents_all_years),
            selected = selectedYear == null,
            onClick = { onYearSelected(null) }
        )
        years.forEach { year ->
            YearChip(
                label = year.toString(),
                selected = selectedYear == year,
                onClick = { onYearSelected(year) }
            )
        }
    }
}

@Composable
private fun YearChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(if (selected) AppColor.StrikePrimary else AppColor.StrikeCard)
            .clickable(
                indication = null,
                interactionSource = remember { MutableInteractionSource() },
                onClick = onClick
            )
            .padding(horizontal = 14.dp, vertical = 6.dp),
        contentAlignment = Alignment.Center
    ) {
        TextNormal(
            text = label,
            fontSize = 12.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
            color = if (selected) Color.White else AppColor.StrikeText.copy(alpha = 0.7f)
        )
    }
}

// ══════════════════════════════════════════════
// Project list tab — shared by Keraunic & Nsg
// ══════════════════════════════════════════════

@Composable
private fun ProjectListTab(
    state: DocumentsState,
    known: List<KnownSite>,
    type: DocumentHistoryType,
    documentLabel: StringResource,
    searchQuery: String,
    year: Int?,
    onOpenDocument: (DocumentHistoryItem) -> Unit,
) {
    when {
        state.isLoading -> LoadingCenter()
        state.error != null -> ErrorCenter(state.error)
        else -> SitesList(
            history = state.history,
            known = known,
            type = type,
            documentLabel = documentLabel,
            searchQuery = searchQuery,
            year = year,
            onOpenDocument = onOpenDocument,
        )
    }
}

// ══════════════════════════════════════════════
// Compact list of sites — shared by the 3 tabs
// ══════════════════════════════════════════════

/**
 * One compact row per site, paginated by [SitesPageSize]: by default only the sites having documents, plus the
 * matching ones when a search is typed. There is no generation button here: documents are generated from the
 * "New document" button of the screen.
 */
@Composable
private fun SitesList(
    history: List<DocumentHistoryItem>,
    known: List<KnownSite>,
    type: DocumentHistoryType,
    documentLabel: StringResource,
    searchQuery: String,
    year: Int?,
    onOpenDocument: (DocumentHistoryItem) -> Unit,
) {
    val entries = remember(known, history, type, searchQuery, year) {
        buildSiteEntries(known, groupDocumentsBySite(history, type, searchQuery, year, ::documentYear), searchQuery)
    }
    // Back to the first page when the tab, the search or the year changes
    var visibleCount by remember(type, searchQuery, year) { mutableStateOf(SitesPageSize) }
    val page = remember(entries, visibleCount) { pageOfSites(entries, visibleCount) }

    LazyColumn(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 16.dp),
    ) {
        item(key = "top-space") { Spacer(Modifier.height(8.dp)) }

        if (entries.isEmpty()) {
            item(key = "empty") {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 24.dp),
                    contentAlignment = Alignment.Center
                ) {
                    TextNormal(
                        text = stringResource(
                            if (searchQuery.isBlank()) {
                                Res.string.documents_no_document
                            } else {
                                Res.string.documents_no_site
                            }
                        ),
                        fontSize = 14.sp,
                        color = AppColor.StrikeText.copy(alpha = 0.5f)
                    )
                }
            }
        }

        items(page.visible, key = { it.key }) { entry ->
            SiteRow(
                entry = entry,
                documentLabel = documentLabel,
                onOpenDocument = onOpenDocument,
            )
        }

        if (page.remaining > 0) {
            item(key = "show-more") {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 12.dp),
                    contentAlignment = Alignment.Center
                ) {
                    OutlinedPillButton(
                        text = stringResource(Res.string.documents_show_more, page.remaining.toString()),
                        onClick = { visibleCount = nextVisibleCount(visibleCount) }
                    )
                }
            }
        }

        item(key = "bottom-space") { Spacer(Modifier.height(16.dp)) }
    }
}

/** Compact row of a site, folded by default: tap it to list its documents (newest first). */
@Composable
private fun SiteRow(
    entry: SiteEntry,
    documentLabel: StringResource,
    onOpenDocument: (DocumentHistoryItem) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val hasDocuments = entry.documents.isNotEmpty()
    val last = entry.documents.firstOrNull()?.let { formatDocumentDay(it.createdAtSecondsUtc) }
    val typeLabel = stringResource(documentLabel)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppColor.StrikeCard)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(enabled = hasDocuments) { expanded = !expanded }
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(modifier = Modifier.width(14.dp)) {
                TextNormal(
                    text = if (!hasDocuments) "" else if (expanded) "⌄" else "›",
                    fontSize = 16.sp,
                    color = AppColor.StrikeText.copy(alpha = 0.5f)
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = entry.name.ifBlank { stringResource(Res.string.documents_unnamed_site) },
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = AppColor.StrikeText
                )
                TextNormal(
                    text = if (last != null) {
                        stringResource(Res.string.documents_last, last)
                    } else {
                        stringResource(Res.string.documents_no_document)
                    },
                    fontSize = 11.sp,
                    color = AppColor.StrikeText.copy(alpha = 0.5f)
                )
            }
            if (hasDocuments) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(10.dp))
                        .background(AppColor.StrikePrimary.copy(alpha = 0.12f))
                        .padding(horizontal = 8.dp, vertical = 2.dp)
                ) {
                    TextNormal(
                        text = stringResource(Res.string.documents_badge, entry.documents.size.toString()),
                        fontSize = 11.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = AppColor.StrikePrimary
                    )
                }
            }
        }

        if (expanded) {
            entry.documents.forEach { document ->
                DocumentRow(
                    label = typeLabel,
                    item = document,
                    onClick = { onOpenDocument(document) }
                )
            }
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.dp)
                .background(AppColor.StrikeText.copy(alpha = 0.08f))
        )
    }
}

@Composable
private fun DocumentRow(
    label: String,
    item: DocumentHistoryItem,
    onClick: () -> Unit,
) {
    val dateText = remember(item.createdAtSecondsUtc) { formatDocumentDate(item.createdAtSecondsUtc) }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppColor.StrikeBackground)
            .clickable(onClick = onClick)
            .padding(start = 34.dp, end = 12.dp, top = 8.dp, bottom = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextNormal(
            text = "$label · $dateText",
            fontSize = 12.sp,
            color = AppColor.StrikeText.copy(alpha = 0.8f)
        )
        TextNormal(
            text = stringResource(Res.string.documents_open),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            color = AppColor.StrikePrimary
        )
    }
}

/** Small outlined button used for "Show more". */
@Composable
private fun OutlinedPillButton(
    text: String,
    onClick: () -> Unit,
    compact: Boolean = false,
) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .border(1.dp, AppColor.StrikePrimary, RoundedCornerShape(8.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = if (compact) 10.dp else 18.dp, vertical = if (compact) 5.dp else 8.dp),
        contentAlignment = Alignment.Center
    ) {
        TextNormal(
            text = text,
            fontSize = if (compact) 12.sp else 13.sp,
            fontWeight = FontWeight.SemiBold,
            color = AppColor.StrikePrimary
        )
    }
}

// ══════════════════════════════════════════════
// Lightning certificates tab
// ══════════════════════════════════════════════

@Composable
private fun LightningCertificatesTab(
    state: DocumentsState,
    known: List<KnownSite>,
    searchQuery: String,
    year: Int?,
    onOpenCertificate: (DocumentHistoryItem) -> Unit,
) {
    val type = DocumentHistoryType.CERT_LIGHTNING
    val hasCertificates = remember(state.history) { state.history.any { it.type == type } }

    if (!state.isHistoryReady) {
        LoadingCenter()
        return
    }

    if (!hasCertificates) {
        // Empty state: documents are generated from the "New document" button
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                TextNormal(text = "\u26A1", fontSize = 32.sp)
                Spacer(Modifier.height(8.dp))
                TextNormal(
                    text = stringResource(Res.string.menu_sr_certificates),
                    fontSize = 16.sp,
                    fontWeight = FontWeight.Bold,
                    color = AppColor.StrikeText
                )
            }
        }
    } else {
        SitesList(
            history = state.history,
            known = known,
            type = type,
            documentLabel = Res.string.menu_sr_certificates,
            searchQuery = searchQuery,
            year = year,
            onOpenDocument = onOpenCertificate,
        )
    }
}

// ══════════════════════════════════════════════
// Date formatting — KMP-safe (no String.format)
// ══════════════════════════════════════════════

private fun documentYear(createdAtSecondsUtc: Long): Int =
    DateTime.fromUnixMillis(createdAtSecondsUtc * 1000L).local.yearInt

private fun formatDocumentDay(createdAtSecondsUtc: Long): String =
    formatDocumentDate(createdAtSecondsUtc).substringBefore(' ')

private fun formatDocumentDate(createdAtSecondsUtc: Long): String {
    val dt = DateTime.fromUnixMillis(createdAtSecondsUtc * 1000L).local
    val day = dt.dayOfMonth.toString().padStart(2, '0')
    val month = dt.month1.toString().padStart(2, '0')
    val year = dt.yearInt
    val hour = dt.hours.toString().padStart(2, '0')
    val min = dt.minutes.toString().padStart(2, '0')

    return "$day/$month/$year $hour:$min"
}

// ══════════════════════════════════════════════
// Shared composables
// ══════════════════════════════════════════════

@Composable
private fun LoadingCenter() {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        CircularProgressIndicator(color = AppColor.StrikePrimary)
    }
}

@Composable
private fun ErrorCenter(msg: String?) {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        TextNormal(
            text = msg ?: stringResource(Res.string.documents_unknown_error),
            fontSize = 14.sp,
            color = AppColor.StrikeDanger
        )
    }
}

@Composable
private fun EmptyCenter(msg: String) {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        TextNormal(
            text = msg,
            fontSize = 14.sp,
            color = AppColor.StrikeText.copy(alpha = 0.5f)
        )
    }
}
